import { Outlet, type RouteObject } from "react-router-dom";
import { RootRedirect } from "./components/RootRedirect.component";
import { authRoutes } from "./features/auth/auth.routes";
import { adminRoutes } from "./features/orgaAdmin/admin.routes";
import { invitationRoutes } from "./features/invitation/invitation.routes";
import DashboardPage from "./features/dashboard/KorrekturDashboard.page";
import KorrekturOverviewPage from "./features/dashboard/KorrekturOverview.page";
import { RequireAuth } from "./components/authWrappers.component";
import { PlatformManagerDashboardPage } from "./features/platformManager/pages/PlatformManagerDashboard.page";
import PlatformManagerOrganizationPage from "./features/platformManager/pages/PlatformManagerOrganization.page";
import KorrekturPage from "./features/korrektur/Korrektur.page";
import ProfilePage from "./features/profile/Profile.page";
import KlausurenPage from "./features/klausur/pages/Klausuren.page";
import KlausurEditorPage from "./features/klausur/pages/KlausurEditor.page";

/**
 * This is our main routes configuration.
 * It is used to define the routes of the application.
 * The routes are defined in the `routes` array.
 * Each route is an object that defines the path, element and children.
 * The children are the routes that are nested under the current route.
 * The element is the component that is rendered when the route is matched.
 *
 * Auth routes are all routes that are related to the authentication of the user.
 * They are nested under the `/auth` path.
 * If the user is authenticated when accessing the root path, they will be redirected to the dashboard.
 * Otherwise, they will be redirected to the login page. This is done with the RootRedirect component.
 *
 */
export const routes: RouteObject[] = [
	{
		path: "/",
		element: <Outlet />,
		children: [
			// Add the additional routes here (structured like the authRoutes object)
			{
				path: "/",
				element: <RootRedirect />,
			},
			{
				path: "/auth",
				element: <Outlet />,
				children: [...authRoutes],
			},
			{
				path: "/admin",
				element: <Outlet />,
				children: [
					{
						path: "",
						element: <Outlet />,
						children: [...adminRoutes],
					},
				],
			},
			{
				path: "/dashboard",
				element: (
					<RequireAuth>
						<DashboardPage />
					</RequireAuth>
				),
			},
			{
				path: "/platformManager/dashboard",
				element: (
					<RequireAuth>
						<PlatformManagerDashboardPage />
					</RequireAuth>
				),
			},
			{
				path: "/platformManager/organizations",
				element: (
					<RequireAuth>
						<PlatformManagerOrganizationPage />
					</RequireAuth>
				),
			},
			{
				path: "/korrektur-overview/:klausurId",
				element: (
					<RequireAuth>
						<KorrekturOverviewPage />
					</RequireAuth>
				),
			},
			{
				path: "/korrektur/:korrekturStepId",
				element: (
					<RequireAuth>
						<KorrekturPage />
					</RequireAuth>
				),
			},
			{
				path: "/invitation",
				element: <Outlet />,
				children: [...invitationRoutes],
			},
			{
				path: "/profile",
				element: (
					<RequireAuth>
						<ProfilePage />
					</RequireAuth>
				),
			},
			{
				path: "/orga-admin/klausur",
				element: (
					<RequireAuth>
						<KlausurenPage />
					</RequireAuth>
				),
			},
			{
				path: "/orga-admin/klausur/:klausurId",
				element: (
					<RequireAuth>
						<KlausurEditorPage />
					</RequireAuth>
				),
			},
		],
	},
];
